import { Fragment, useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { colorFromName, initials } from "@/lib/format";
import { contactDisplay, leadDisplayName } from "@/lib/contact";
import type { PipelinePosition } from "@/contexts/PipelineContext";
import LeadTagChips from "@/components/leadTags/LeadTagChips";
import QuienAtiendeBadge from "./QuienAtiendeBadge";
import PositionColumn from "./PositionColumn";
import {
  deriveBadge,
  headerColorForStage,
  relativeFrom,
  type TableroCardLead,
  type TableroLead,
  type TableroQueryFilters,
} from "./types";

// Flat presentational list: the loaded rows of all columns merged, most
// recently messaged first.
export const ListaRows = ({
  rows,
  nonRealtimeIds,
  positionById,
  employeeNameById,
  onSelectLead,
  selectedLeadId,
  isLoading,
  isError,
}: {
  rows: TableroCardLead[];
  /** Rows matched by name in Tinybird rather than by handle in DynamoDB. */
  nonRealtimeIds?: Set<string>;
  positionById: Map<string, PipelinePosition>;
  employeeNameById: Map<string, string>;
  onSelectLead: (leadId: string) => void;
  selectedLeadId?: string;
  isLoading?: boolean;
  /** Same story as the Kanban column: a failed load must not read as "no matches". */
  isError?: boolean;
}) => {
  // Realtime rows first, each group by recency. One flat list keeps the row
  // markup identical; the divider below is inserted where the groups meet.
  const ordered = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          Number(!!nonRealtimeIds?.has(a.id)) - Number(!!nonRealtimeIds?.has(b.id)) ||
          (b.lastLeadMessageAt ?? "").localeCompare(a.lastLeadMessageAt ?? "")
      ),
    [rows, nonRealtimeIds]
  );
  // Absent when the name half added nothing, so an ordinary phone or username
  // search reads as one clean list.
  const firstNonRealtimeId = ordered.find((r) => nonRealtimeIds?.has(r.id))?.id;

  if (!isLoading && isError && ordered.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-sm text-red-600">
        No pudimos cargar los leads.
      </div>
    );
  }

  if (!isLoading && ordered.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-sm text-neutral-600">
        Ningún lead coincide con los filtros aplicados
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <div className="min-w-[900px]">
        {ordered.map((lead) => {
          const position = positionById.get(lead.pipelinePositionId);
          const displayName = leadDisplayName(lead);
          const contact = contactDisplay(lead);
          const sel = selectedLeadId === lead.id;
          return (
            <Fragment key={lead.id}>
              {lead.id === firstNonRealtimeId && (
                <div className="border-b border-neutral-100 bg-neutral-50 px-4 py-2 text-xs text-neutral-600">
                  Coincidencias por nombre · datos no en tiempo real
                </div>
              )}
            <button
              onClick={() => onSelectLead(lead.id)}
              className={cn(
                "flex h-[72px] w-full items-center gap-3 border-b border-neutral-100 px-4 text-left transition-colors",
                sel ? "bg-palomma-primary/5" : "hover:bg-neutral-100"
              )}
            >
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                style={{ background: colorFromName(displayName) }}
              >
                {initials(displayName)}
              </div>
              <div className="w-[200px] shrink-0">
                <div className="truncate text-sm font-semibold text-neutral-900">{displayName}</div>
                <div className="truncate text-xs text-neutral-600">
                  {contact.kind === "none" ? "" : contact.value}
                </div>
              </div>
              <div className="min-w-0 max-w-[400px] flex-1">
                <div className="truncate text-sm italic text-neutral-600">
                  {lead.lastMessagePreview ?? ""}
                </div>
                <LeadTagChips tagIds={lead.tagIds} max={2} className="mt-1 flex-nowrap overflow-hidden" />
              </div>
              <div className="w-[180px] shrink-0">
                <span
                  className={cn(
                    "inline-block rounded-pill px-2 py-1 text-xs font-medium text-neutral-900",
                    position ? headerColorForStage(position.stage) : "bg-neutral-100"
                  )}
                >
                  {position?.displayName ?? lead.pipelinePositionId}
                </span>
              </div>
              <div className="w-[180px] shrink-0">
                <QuienAtiendeBadge
                  badge={deriveBadge(lead)}
                  asesor={lead.employeeId ? employeeNameById.get(lead.employeeId) : undefined}
                />
              </div>
              <div className="w-[100px] shrink-0 text-right text-xs text-neutral-600">
                {relativeFrom(lead.lastLeadMessageAt)}
              </div>
            </button>
            </Fragment>
          );
        })}
        {isLoading ? (
          <div className="p-8 text-center text-sm text-neutral-600">Cargando…</div>
        ) : null}
      </div>
    </div>
  );
};

// Lista view over the per-position queries: mounts one hidden fetcher per
// position (same cache keys as the Kanban columns, so contents always match)
// and merges whatever pages are loaded.
const ListaView = ({
  merchantId,
  pipelineId,
  positions,
  filters,
  employeeNameById,
  onSelectLead,
  selectedLeadId,
}: {
  merchantId: string;
  pipelineId: string;
  positions: PipelinePosition[];
  filters: TableroQueryFilters;
  employeeNameById: Map<string, string>;
  onSelectLead: (leadId: string) => void;
  selectedLeadId?: string;
}) => {
  const [statusByPosition, setStatusByPosition] = useState<
    Map<string, { leads: TableroLead[]; isLoading: boolean }>
  >(new Map());

  const handleLeadsChange = useCallback(
    (positionId: string, leads: TableroLead[], isLoading: boolean) => {
      setStatusByPosition((prev) => {
        const next = new Map(prev);
        next.set(positionId, { leads, isLoading });
        return next;
      });
    },
    []
  );

  const positionById = useMemo(
    () => new Map(positions.map((p) => [p.id, p])),
    [positions]
  );

  const rows = useMemo(
    () =>
      positions.flatMap((p) => statusByPosition.get(p.id)?.leads ?? []),
    [positions, statusByPosition]
  );

  const isLoading = positions.some((p) => statusByPosition.get(p.id)?.isLoading !== false);

  return (
    <>
      {positions.map((position) => (
        <PositionColumn
          key={position.id}
          merchantId={merchantId}
          pipelineId={pipelineId}
          position={position}
          filters={filters}
          mode="hidden"
          draggable={false}
          droppable={false}
          onSelectLead={onSelectLead}
          employeeNameById={employeeNameById}
          onLeadsChange={handleLeadsChange}
        />
      ))}
      <ListaRows
        rows={rows}
        positionById={positionById}
        employeeNameById={employeeNameById}
        onSelectLead={onSelectLead}
        selectedLeadId={selectedLeadId}
        isLoading={isLoading}
      />
    </>
  );
};

export default ListaView;
